import type { FC } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '@/routes/route.config';
import type { SiteLink } from '@/types/home.types';

interface SiteHeaderProps {
  links: SiteLink[];
  ctaLabel: string;
  ctaHref: string;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
}

/* No display utility here: Tailwind emits `inline-flex` after `hidden`, so
   baking one in would beat the `hidden` that hides this at narrow widths. */
const ctaClass =
  'items-center justify-center border border-transparent bg-site-accent px-[14.4px] py-2 font-site text-sm leading-[1.2] font-extrabold whitespace-nowrap text-white transition-colors hover:bg-site-accent-deep';

const SiteHeader: FC<SiteHeaderProps> = ({
  links,
  ctaLabel,
  ctaHref,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu,
}) => (
  <header className="sticky top-0 z-50 border-b-2 border-site-line bg-site-bg">
    <div className="flex w-full items-center gap-6 px-[clamp(20px,5vw,72px)] py-3">
      <Link to={ROUTES.home} className="flex items-center" aria-label="Shoutt home">
        <img
          src="/shoutt-lockup.svg"
          alt="Shoutt — powered by people around you"
          width={98}
          height={32}
          className="block h-8 w-auto"
        />
      </Link>

      <nav className="ml-auto hidden items-center gap-6 lg:flex" aria-label="Primary">
        {links.map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="font-site text-[15px] whitespace-nowrap text-site-ink transition-colors hover:text-site-accent"
          >
            {link.label}
          </a>
        ))}
        <a href={ctaHref} className={`inline-flex ${ctaClass}`}>
          {ctaLabel}
        </a>
      </nav>

      <a href={ctaHref} className={`ml-auto hidden sm:inline-flex lg:hidden ${ctaClass}`}>
        {ctaLabel}
      </a>

      <button
        type="button"
        onClick={onToggleMenu}
        aria-expanded={isMenuOpen}
        aria-controls="site-menu"
        aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        className="ml-auto inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center border-2 border-site-ink text-site-ink sm:ml-0 lg:hidden"
      >
        <svg
          aria-hidden
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="square"
        >
          {isMenuOpen ? <path d="M5 5l14 14M19 5L5 19" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
        </svg>
      </button>
    </div>

    {isMenuOpen ? (
      <nav
        id="site-menu"
        aria-label="Primary"
        className="border-t-2 border-site-line bg-site-bg lg:hidden"
      >
        <ul className="w-full px-[clamp(20px,5vw,72px)] py-2">
          {links.map((link) => (
            <li key={link.href} className="border-b border-site-line/50 last:border-b-0">
              <a
                href={link.href}
                onClick={onCloseMenu}
                className="block py-3 font-site text-base text-site-ink transition-colors hover:text-site-accent"
              >
                {link.label}
              </a>
            </li>
          ))}
          <li className="py-3 sm:hidden">
            <a href={ctaHref} onClick={onCloseMenu} className={`flex w-full ${ctaClass}`}>
              {ctaLabel}
            </a>
          </li>
        </ul>
      </nav>
    ) : null}
  </header>
);

export default SiteHeader;
